import { supabase } from '@/lib/supabase';
import { ReturnRecord, Sale, PaginatedResult } from '@/types/models';
import { PAGE_SIZE } from '@/lib/constants';

export interface FetchReturnsParams {
  search?: string;
  status?: string;
  page?: number;
}

export async function fetchReturns(
  params: FetchReturnsParams
): Promise<PaginatedResult<ReturnRecord>> {
  const { search = '', status = '', page = 0 } = params;
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  let query = supabase
    .from('returns')
    .select(
      '*, sale:sales(id, invoice_number, sale_date), customer:customers(id, name, phone)',
      { count: 'exact' }
    )
    .order('return_date', { ascending: false })
    .range(from, to);

  if (search.trim()) {
    const s = search.trim();
    query = query.or(`return_number.ilike.%${s}%,reason.ilike.%${s}%,notes.ilike.%${s}%`);
  }

  if (status) query = query.eq('status', status);

  const { data, error, count } = await query;
  if (error) throw error;

  return { data: (data ?? []) as ReturnRecord[], count: count ?? 0 };
}

export async function fetchReturnById(id: string): Promise<ReturnRecord> {
  const { data, error } = await supabase
    .from('returns')
    .select(
      '*, sale:sales(id, invoice_number, sale_date, total, status), customer:customers(id, name, phone, email, address), items:return_items(*, sale_item:sale_items(*))'
    )
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as ReturnRecord;
}

export async function fetchEligibleSalesForReturn(search = ''): Promise<Sale[]> {
  let query = supabase
    .from('sales')
    .select(
      '*, customer:customers(id, name, phone), items:sale_items(*, laptop:laptops(id, stock_id, brand, model, serial_number), accessory:accessories(id, sku, name))'
    )
    .in('status', ['completed', 'partially_refunded'])
    .order('sale_date', { ascending: false })
    .limit(50);

  if (search.trim()) {
    const s = search.trim();
    query = query.or(`invoice_number.ilike.%${s}%,notes.ilike.%${s}%`);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as Sale[];
}

export async function fetchPreviousReturnedQuantitiesForSale(
  saleId: string
): Promise<Record<string, number>> {
  // Query all return_items for completed returns of this sale
  const { data, error } = await supabase
    .from('return_items')
    .select('sale_item_id, quantity, return:returns!inner(status, sale_id)')
    .eq('return.sale_id', saleId)
    .eq('return.status', 'completed');

  if (error) throw error;

  const map: Record<string, number> = {};
  (data ?? []).forEach((row: any) => {
    const itemId = row.sale_item_id;
    map[itemId] = (map[itemId] || 0) + (row.quantity || 0);
  });

  return map;
}

/**
 * process_return RPC — exact signature & JSON keys from 01_SUPABASE_SCHEMA_V2.sql:
 *
 * p_sale_id uuid,
 * p_items jsonb,
 * p_refund_amount numeric,
 * p_refund_method text,
 * p_reason text,
 * p_notes text
 *
 * Each p_items JSON object keys read by function body:
 *   sale_item_id (uuid)
 *   quantity (integer)
 *   restocked (boolean)
 *   unit_refund (numeric)
 *   condition_on_return (text)
 *   notes (text)
 */
export interface ProcessReturnPayload {
  sale_id: string;
  items: Array<{
    sale_item_id: string;
    quantity: number;
    restocked: boolean;
    unit_refund?: number;
    condition_on_return?: string | null;
    notes?: string | null;
  }>;
  refund_amount?: number | null;
  refund_method?: string;
  reason?: string | null;
  notes?: string | null;
}

export async function processReturnRpc(payload: ProcessReturnPayload): Promise<{
  return_id: string;
  return_number: string;
  return_value: number;
  refund_amount: number;
}> {
  const { data, error } = await supabase.rpc('process_return', {
    p_sale_id: payload.sale_id,
    p_items: payload.items,
    p_refund_amount: payload.refund_amount ?? null,
    p_refund_method: payload.refund_method ?? 'cash',
    p_reason: payload.reason ?? null,
    p_notes: payload.notes ?? null,
  });

  if (error) throw error;
  return data as {
    return_id: string;
    return_number: string;
    return_value: number;
    refund_amount: number;
  };
}
